import {
  BookingAnalyticsData,
  DiningBookingInformation,
  GolfBookingInformation,
  PrivateBookingInformation,
  PublicBookingInformation,
} from "@/screens/BookingDetail/NewPatronBookingFlow/types";

import { isAnalyticsTrackingAllowed } from "../analyticsTrackingConsent";
import {
  ConversionAnalyticsConfig,
  ConversionAnalyticsGlobals,
  ConversionEventInput,
  NormalizedConversionEvent,
} from "./types";

const validGtmId = /^GTM-[A-Z0-9]+$/;
const validGa4Id = /^G-[A-Z0-9]+$/;
const validMetaPixelId = /^\d{5,30}$/;
const supportedConversionEvents = new Set([
  "booking_confirmed",
  "multi_booking_confirmed",
  "dining_reservation_confirmed",
]);

const omitUndefined = <T extends Record<string, unknown>>(obj: T): T =>
  Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== undefined)
  ) as T;

const getBrowserGlobals = (): ConversionAnalyticsGlobals => {
  if (typeof window === "undefined") return {};

  return {
    dataLayer:
      window.__whooshConversionAnalytics?.activeDataLayer ?? window.dataLayer,
    gtag: window.gtag,
    fbq: window.fbq,
    conversionAnalyticsConfig:
      window.__whooshConversionAnalytics?.conversionAnalyticsConfig,
    seenEventIds: window.__whooshConversionAnalytics?.seenEventIds,
    activeScopeId: window.__whooshConversionAnalytics?.activeScopeId,
    analyticsTrackingAllowed: isAnalyticsTrackingAllowed(),
  };
};

const isPublicBookingInformation = (
  information: PublicBookingInformation | PrivateBookingInformation
): information is PublicBookingInformation =>
  "knownCustomerCount" in information;

const isGolfBookingInformation = (
  information: NonNullable<BookingAnalyticsData>["activityTypeMetadata"]
): information is GolfBookingInformation => "holeCounts" in information;

const isDiningBookingInformation = (
  information: NonNullable<BookingAnalyticsData>["activityTypeMetadata"]
): information is DiningBookingInformation => "partySize" in information;

const getEventId = (input: ConversionEventInput): string => {
  const identifier =
    input.transactionId ??
    input.orderId ??
    input.bookingId ??
    input.bookingIds?.slice().sort().join(",") ??
    "unknown";

  return `${input.eventName}:${identifier}`;
};

export const createNormalizedConversionEvent = (
  input: ConversionEventInput
): NormalizedConversionEvent => {
  const { bookingAnalyticsData: data } = input;
  const playerInformation = data.playerInformation;
  const event: NormalizedConversionEvent = {
    event_name: input.eventName,
    event_id: getEventId(input),
    occurred_at: input.occurredAt ?? new Date().toISOString(),
    source: "patron_web",
    club_id: data.clubId,
    club_slug: data.clubSlug,
    booking_id: input.bookingId,
    booking_ids: input.bookingIds,
    booking_type: data.bookingType,
    booking_status: "CONFIRMED",
    facility_type: data.facilityType,
    booking_department_type: data.bookingDepartmentType,
    is_public_booking: data.isPublicBooking,
    is_request: data.isRequest,
    prepayment_requirement:
      data.paymentInformation?.requiresPrePayment ?? "UNKNOWN",
    participant_count: playerInformation.playerCount,
    num_items: input.bookingIds?.length ?? 1,
    order_id: input.orderId,
    transaction_id: input.transactionId,
    value: input.value,
    currency:
      input.value === undefined ? undefined : input.currency?.toUpperCase(),
  };

  if (isPublicBookingInformation(playerInformation)) {
    event.known_customer_count = playerInformation.knownCustomerCount;
    event.unknown_customer_count = playerInformation.unknownCustomerCount;
  } else {
    event.known_member_count = playerInformation.knownMemberCount;
    event.unknown_member_count = playerInformation.unknownMemberCount;
    event.known_guest_count = playerInformation.knownGuestCount;
    event.unknown_guest_count = playerInformation.unknownGuestCount;
  }

  if (isGolfBookingInformation(data.activityTypeMetadata)) {
    event.hole_count_9 = data.activityTypeMetadata.holeCounts[9];
    event.hole_count_18 = data.activityTypeMetadata.holeCounts[18];
    event.transportation_count = Object.values(
      data.activityTypeMetadata.transportation
    ).reduce((total, count) => total + count, 0);
  } else if (isDiningBookingInformation(data.activityTypeMetadata)) {
    event.participant_count = data.activityTypeMetadata.partySize ?? 0;
  } else {
    event.duration_mins = data.activityTypeMetadata.duration;
  }

  return event;
};

export const sanitizeConversionEvent = (
  event: NormalizedConversionEvent
): NormalizedConversionEvent => {
  const sanitized = omitUndefined(event);

  if (sanitized.value !== undefined && !sanitized.currency) {
    delete sanitized.value;
  }

  return sanitized;
};

const getConfiguredProvider = (
  value: string | null | undefined,
  pattern: RegExp
): string | undefined => {
  const normalizedValue = value?.trim().toUpperCase();
  return normalizedValue && pattern.test(normalizedValue)
    ? normalizedValue
    : undefined;
};

export const trackConversionEvent = (
  event: NormalizedConversionEvent,
  config: ConversionAnalyticsConfig | undefined,
  globals: ConversionAnalyticsGlobals = getBrowserGlobals()
): void => {
  if (
    globals.activeScopeId !== undefined &&
    globals.activeScopeId !== event.club_id
  ) {
    return;
  }

  if (globals.analyticsTrackingAllowed === false) return;

  const trackingConfig = config ?? globals.conversionAnalyticsConfig;

  if (!trackingConfig?.enabled) return;
  if (!supportedConversionEvents.has(event.event_name)) return;

  const sanitizedEvent = sanitizeConversionEvent(event);
  const gtmContainerId = getConfiguredProvider(
    trackingConfig.gtmContainerId,
    validGtmId
  );
  const ga4MeasurementId = getConfiguredProvider(
    trackingConfig.ga4MeasurementId,
    validGa4Id
  );
  const metaPixelId = getConfiguredProvider(
    trackingConfig.metaPixelId,
    validMetaPixelId
  );

  if (
    !(
      (gtmContainerId && globals.dataLayer) ||
      (ga4MeasurementId && globals.gtag) ||
      (metaPixelId && globals.fbq)
    )
  ) {
    return;
  }

  if (globals.seenEventIds?.has(sanitizedEvent.event_id)) return;
  globals.seenEventIds?.add(sanitizedEvent.event_id);

  if (gtmContainerId && globals.dataLayer) {
    globals.dataLayer.push({
      event: sanitizedEvent.event_name,
      whoosh: sanitizedEvent,
    });
  }

  if (ga4MeasurementId && globals.gtag) {
    const eventName = sanitizedEvent.is_request
      ? "generate_lead"
      : sanitizedEvent.value !== undefined && sanitizedEvent.transaction_id
      ? "purchase"
      : sanitizedEvent.event_name;
    const parameters = {
      ...sanitizedEvent,
      whoosh_event_name: sanitizedEvent.event_name,
      send_to: ga4MeasurementId,
      ...(eventName === "purchase"
        ? {
            transaction_id: sanitizedEvent.transaction_id,
            value: sanitizedEvent.value,
            currency: sanitizedEvent.currency,
            items: [
              {
                item_id: sanitizedEvent.booking_id,
                quantity: sanitizedEvent.num_items,
              },
            ],
          }
        : {}),
    };
    globals.gtag("event", eventName, parameters);
  }

  if (metaPixelId && globals.fbq) {
    const eventName = sanitizedEvent.is_request
      ? "Lead"
      : sanitizedEvent.value !== undefined && sanitizedEvent.currency
      ? "Purchase"
      : "Schedule";
    const parameters =
      eventName === "Purchase"
        ? {
            value: sanitizedEvent.value,
            currency: sanitizedEvent.currency,
            content_type: "booking",
            content_ids: sanitizedEvent.booking_ids ?? [
              sanitizedEvent.booking_id,
            ],
            num_items: sanitizedEvent.num_items,
          }
        : {
            content_type: "booking",
            content_ids: sanitizedEvent.booking_ids ?? [
              sanitizedEvent.booking_id,
            ],
            num_items: sanitizedEvent.num_items,
          };
    globals.fbq("trackSingle", metaPixelId, eventName, parameters);
  }
};
